import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_SINGLE_USER_PERMISSION } from '../../../../utils/apollo/permission';
import {
  IUserPermissionFieldsFragment,
  IUserPermissionQuery,
  IUserPermissionQueryVariables,
  PermissionKey,
  UserState,
} from '../../../../utils/apollo/graphqlSchema';
import {
  ClickablePageTitle,
  PageHeaderDivider,
} from '../../../../components/PageHeader';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import * as PermissionControlsUI from '../../../PermissionControls/PermissionControlsUI';
import { appRoutes } from '../../../../path';
import styled from 'styled-components';
import { batch } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setDictionaryListAction } from '../../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../../utils/interfaces';
import DisableUserModal from '../components/DisableUserModal';
import UserProfileCard from '../components/UserProfileCard';
import PermissionEntityDescribeTable from '../../../PermissionControls/PermissionEntityDescribeTable';
import PermissionSchemasSelector from '../../../PermissionControls/PermissionSchemasSelector';
import ExpandableTable from '../../../../components/ExpandableTable';
import {
  usePermissionEntityDescribeTable,
  usePermissionOverviewList,
} from '../../../PermissionControls/hooks';
import { useBootstrapSelector } from '../../../../redux/selectorHooks';
import { IBasicSchema } from '../../../PermissionControls/types';
import { selectOpenedModalType } from '../../../../redux/selectors/modal';
import { ModalSliceType, openModal } from '../../../../redux/slices/modal';
import { DangerButton, PrimaryButton } from '../../../../components/Button';
import PermissionElementWrapper from '../../../PermissionControls/PermissionElementWrapper';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import { ContentLoader } from '../../../../components/Loaders';
import { theme } from '../../../../styles/theme';
import ThinDivider from '../../../../components/ThinDivider';
import PermissionToggleModal from '../../../PermissionControls/PermissionToggleModal';
import KubitTooltip from '../../../../components/KubitTooltip';
import useCustomerPlan, {
  getMaxSeatsTooltip,
} from '../../../../utils/hooks/useCustomerPlan';

interface IProps {
  userId: string;
  readOnly?: boolean;
}

const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 320px auto;
  column-gap: 12px;
`;

const TableHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;

const StyledDangerButton = styled(DangerButton)`
  width: 120px;
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  width: 120px;
`;

/**
 * UserOverview is the page for viewing a single user's permissions.
 */
const UserOverview = ({ userId, readOnly }: IProps) => {
  const dispatch = useAppDispatch();
  const { checkIsPermitted } = usePermission();
  const isPermittedToDisableAndEnableUser = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminDelete],
  });

  const [user, setUser] = useState<IUserPermissionFieldsFragment | null>(null);

  const { maxSeats, refetchCustomerPlan, reachMaxSeats } = useCustomerPlan();
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const isDisableUserModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.DisableUser, ''),
  );
  const isEnableUserModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.EnableUser, ''),
  );
  const [schemaFilter, setSchemaFilter] = useState<IBasicSchema>(
    permissionConfig.allSchema,
  );
  const { contentList } = usePermissionEntityDescribeTable({
    entity: user,
    noActions: readOnly,
  });
  const totalRoles = user
    ? [...user.roles, ...user.groups.flatMap((group) => group.roles)]
    : [];
  const permissionAssignments = totalRoles.flatMap(
    (role) => role.permissionAssignments,
  );

  const { displayedTableList } = usePermissionOverviewList({
    permissionAssignments,
    schemaFilter,
  });
  const onFetchUser = ({ user }: IUserPermissionQuery) => {
    setUser(user);
    batch(() => {
      dispatch(
        setDictionaryListAction({
          newValues: user.roles,
          dictionaryType: DictionaryTypeEnum.Roles,
        }),
      );
      dispatch(
        setDictionaryListAction({
          newValues: user.groups,
          dictionaryType: DictionaryTypeEnum.UserGroups,
        }),
      );
    });
  };

  const { refetch: refetchUser, loading } = useQuery<
    IUserPermissionQuery,
    IUserPermissionQueryVariables
  >(GET_SINGLE_USER_PERMISSION, {
    variables: {
      id: userId,
    },
    onCompleted: onFetchUser,
  });

  const refetch = useCallback(async () => {
    await Promise.allSettled([refetchUser(), refetchCustomerPlan()]);
  }, [refetchCustomerPlan, refetchUser]);

  const titleList = user
    ? [
        { name: 'Users', path: appRoutes.permission.users },
        { name: user.name, path: '' },
      ]
    : [];
  const onSchemaFilterChange = (schemas: IBasicSchema[]) => {
    assertCondition(schemas.length === 1);
    setSchemaFilter(schemas[0]);
  };

  const maxSeatsTooltipContent = getMaxSeatsTooltip(maxSeats);
  // reset dictionary list on unmount. Right now all forms are using the same dictionary list from redux
  // so we need to reset it. We planned to remove dictionarySlice and use state only for dictionary list.
  useEffect(() => {
    return () => {
      batch(() => {
        dispatch(
          setDictionaryListAction({
            newValues: [],
            dictionaryType: DictionaryTypeEnum.UserGroups,
          }),
        );
        dispatch(
          setDictionaryListAction({
            newValues: [],
            dictionaryType: DictionaryTypeEnum.Roles,
          }),
        );
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <ContentLoader />;
  }
  return (
    <>
      {!readOnly && (
        <>
          <ClickablePageTitle titleList={titleList} />
          <PageHeaderDivider />
        </>
      )}
      {user && (
        <PermissionControlsUI.OverviewContainer>
          <UserProfileCard user={user} isEditing={false} />
          <ThinDivider layout="horizontal" size="full" />
          <PermissionEntityDescribeTable contentList={contentList} />
          <ThinDivider layout="horizontal" size="full" />
          <TableHeaderRow>
            <TableHeader>User Permissions</TableHeader>
            <PermissionSchemasSelector
              setSchemas={onSchemaFilterChange}
              selectedSchemas={[schemaFilter]}
              schemaOptions={permissionConfig.schemas}
              isMulti={false}
            />
          </TableHeaderRow>
          <ExpandableTable tableList={displayedTableList} />
          {!readOnly && (
            <>
              <ThinDivider layout="horizontal" size="full" />
              <PermissionElementWrapper
                isPermitted={isPermittedToDisableAndEnableUser}
                isHalfOpacity={true}
              >
                {user.state === UserState.Deleted && (
                  <KubitTooltip
                    content={maxSeatsTooltipContent}
                    // display tooltip if reachMax and trying to enable the user.
                    // also, not to display 2 tooltips when non-permitted and reachMax.
                    disabled={
                      !isPermittedToDisableAndEnableUser ||
                      !maxSeatsTooltipContent ||
                      !reachMaxSeats
                    }
                  >
                    <StyledPrimaryButton
                      type="button"
                      disabled={reachMaxSeats}
                      onClick={() =>
                        dispatch(
                          openModal({
                            type: ModalSliceType.EnableUser,
                            data: { ...user, userId: user.id },
                          }),
                        )
                      }
                    >
                      Enable User
                    </StyledPrimaryButton>
                  </KubitTooltip>
                )}
                {user.state === UserState.Active && (
                  <StyledDangerButton
                    type="button"
                    onClick={() =>
                      dispatch(
                        openModal({
                          type: ModalSliceType.DisableUser,
                          data: { userId: user.id },
                        }),
                      )
                    }
                    disabled={!isPermittedToDisableAndEnableUser}
                    trackContext="users"
                    trackAction="disable"
                  >
                    Disable User
                  </StyledDangerButton>
                )}
              </PermissionElementWrapper>
            </>
          )}
        </PermissionControlsUI.OverviewContainer>
      )}
      {isDisableUserModalOpen && <DisableUserModal refetch={refetch} />}
      {isEnableUserModalOpen && <PermissionToggleModal refetch={refetch} />}
    </>
  );
};

export default UserOverview;
